/**
 * v4.1 Phase 38.5 — Notifications bell for the app header.
 *
 * Polls the notifications endpoint every 60s while the tab is focused.
 * Unread badge capped at 99+. Clicking a notification marks it read
 * and deep-links to the project (or editor + section for comment
 * events).
 *
 * Gated by COLLAB_MODE_ENABLED at the caller (Header).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../services/notifications.js";
import { useStore } from "../../store/useStore.js";
import "../editor/CommentsSidebar.css";

const POLL_INTERVAL_MS = 60_000;

function formatRelative(iso) {
  if (!iso) return "";
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function eventLabel(n) {
  switch (n.eventType) {
    case "project_member_added":
      return "You were added to a project";
    case "comment_created":
      return "New comment on a project";
    case "comment_reply":
      return "Someone replied to your comment";
    case "comment_resolved":
      return "A comment was resolved";
    case "project_ownership_transferred":
      return "Project ownership transferred";
    default:
      return n.eventType;
  }
}

function deepLinkPath(n) {
  if (!n.projectId) return "/";
  if (n.filename && n.sectionId) {
    // Editor route; section deep-link is handled client-side.
    return `/projects/${n.projectId}?filename=${encodeURIComponent(n.filename)}&section=${encodeURIComponent(n.sectionId)}`;
  }
  return `/projects/${n.projectId}`;
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const pushToast = useStore((s) => s.pushToast);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const pollRef = useRef(null);
  const loading = !hasLoadedOnce;

  const unreadCount = items.filter((n) => !n.readAt).length;
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  const refresh = useCallback(async () => {
    try {
      const data = await listNotifications({ limit: 20 });
      setItems(data || []);
      setHasLoadedOnce(true);
    } catch {
      // Best-effort; quiet on poll failures so we don't spam toasts
      setHasLoadedOnce(true);
    }
  }, []);

  useEffect(() => {
    // Initial fetch. refresh's setState happens in a .then callback
    // but the lint rule can't trace through the Promise chain.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    refresh();
  }, [refresh]);

  useEffect(() => {
    // Poll only while tab focused — respect Page Visibility API.
    function startPoll() {
      if (pollRef.current) return;
      pollRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    }
    function stopPoll() {
      if (!pollRef.current) return;
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    function handleVisibility() {
      if (document.hidden) stopPoll();
      else startPoll();
    }
    if (!document.hidden) startPoll();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      stopPoll();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refresh]);

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.readAt) {
      markNotificationRead(n.notificationId).catch(() => {});
      setItems((prev) =>
        prev.map((x) =>
          x.notificationId === n.notificationId
            ? { ...x, readAt: new Date().toISOString() }
            : x,
        ),
      );
    }
    navigate(deepLinkPath(n));
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) =>
        prev.map((x) =>
          x.readAt ? x : { ...x, readAt: new Date().toISOString() },
        ),
      );
      pushToast("All caught up", "success");
    } catch (err) {
      pushToast(err.message || "Failed to mark all read", "error");
    }
  };

  return (
    <div className="notifications-bell">
      <button
        type="button"
        className="notifications-bell-button"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        🔔
        {unreadCount > 0 && (
          <span className="notifications-bell-badge">{badgeLabel}</span>
        )}
      </button>
      {open && (
        <div className="notifications-dropdown" role="menu">
          <div className="notifications-dropdown-header">
            <h4>Notifications</h4>
            {unreadCount > 0 && (
              <button type="button" className="btn-link" onClick={handleMarkAll}>
                Mark all read
              </button>
            )}
          </div>
          {loading && items.length === 0 ? (
            <p className="empty-hint">Loading…</p>
          ) : items.length === 0 ? (
            <p className="empty-hint">You&apos;re all caught up.</p>
          ) : (
            items.map((n) => (
              <button
                key={n.notificationId}
                type="button"
                className={`notification-item${!n.readAt ? " notification-item--unread" : ""}`}
                onClick={() => handleClick(n)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                }}
              >
                <div className="notification-event">{eventLabel(n)}</div>
                <div className="notification-time">
                  {formatRelative(n.createdAt)}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
